import { useState, useEffect } from 'react';
import axios from 'axios';
import { Wallet, ArrowDownRight, ArrowUpRight, Plus, History, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Transaction {
  id: number;
  title: string;
  amount: number;
  type: 'income' | 'expense';
  date: string;
}

interface WalletData {
  success: boolean;
  balance: number;
  total_earned: number;
  transactions: Transaction[];
}

export default function WalletOSPage() {
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWallet = async () => {
    try {
      const res = await axios.get(`${import.meta.env.VITE_API_URL}/api/wallet/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setWalletData(res.data);
    } catch (err) {
      console.error(err);
      setError('Cüzdan bilgileri alınamadı.');
      toast.error('Cüzdan yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchWallet();
  }, []);

  const handleDeposit = () => {
    toast.success('Ödeme altyapısı (Stripe) entegrasyonu bekleniyor.');
  };

  if (loading) { return <div className="flex justify-center p-20"><Loader2 className="animate-spin text-indigo-500" size={40} /></div>; }
  if (error || !walletData) { return <div className="p-6 text-red-400 flex items-center gap-2"><AlertCircle /> {error}</div>; }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-3 text-white">
          <Wallet className="text-indigo-500" size={32} /> Cüzdan & FLZ
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Bakiye Kartı */}
        <div className="bg-gradient-to-br from-indigo-900 to-purple-900 border border-indigo-500/30 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <Wallet size={120} />
          </div>
          <div className="relative z-10">
            <h3 className="text-indigo-200 text-sm font-bold uppercase tracking-widest mb-2">Mevcut Bakiye</h3>
            <div className="text-5xl font-extrabold text-white mb-6">
              {walletData.balance.toLocaleString()} <span className="text-2xl text-indigo-300">FLZ</span>
            </div>
            
            <div className="flex gap-4">
              <button 
                onClick={handleDeposit}
                className="bg-white text-indigo-900 hover:bg-gray-100 px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-colors shadow-lg"
              >
                <Plus size={18} /> Kredi Yükle
              </button>
              <button className="bg-indigo-800/50 hover:bg-indigo-800 text-white border border-indigo-500/50 px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-colors">
                <ArrowUpRight size={18} /> Çekim Yap
              </button>
            </div>
          </div>
        </div>

        {/* İstatistikler */}
        <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8 shadow-xl flex flex-col justify-center">
          <h3 className="text-gray-400 text-sm font-bold uppercase tracking-widest mb-4">Toplam Kazanç</h3>
          <div className="text-4xl font-bold text-green-400 flex items-center gap-3">
            <ArrowDownRight size={32} />
            +{walletData.total_earned.toLocaleString()} FLZ
          </div>
          <p className="text-sm text-gray-500 mt-4 leading-relaxed">
            Platform üzerindeki research katılımlarından, agent görevlerinden ve bağışlardan kazandığınız toplam miktar.
          </p>
        </div>
      </div>

      {/* İşlem Geçmişi */}
      <div className="bg-gray-900 border border-gray-800 rounded-3xl p-6 shadow-xl">
        <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
          <History className="text-indigo-400" size={24} /> Son İşlemler
        </h3>
        
        <div className="space-y-3">
          {walletData.transactions.length === 0 ? (
            <p className="text-center text-gray-500 py-8 italic">Henüz bir işlem geçmişiniz bulunmuyor.</p>
          ) : walletData.transactions.map((tx) => (
            <div key={tx.id} className="flex items-center justify-between p-4 bg-black/20 hover:bg-gray-800 border border-gray-800/50 rounded-xl transition-colors">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${tx.type === 'income' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                  {tx.type === 'income' ? <ArrowDownRight size={20} /> : <ArrowUpRight size={20} />}
                </div>
                <div>
                  <p className="text-white font-semibold">{tx.title}</p>
                  <p className="text-xs text-gray-500">{new Date(tx.date).toLocaleString('tr-TR')}</p>
                </div>
              </div>
              <div className={`text-lg font-bold ${tx.type === 'income' ? 'text-green-400' : 'text-white'}`}>
                {tx.type === 'income' ? '+' : '-'}{tx.amount} FLZ
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}